import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'

import type { Currency } from '@/bindings/Currency'
import type { SessionSummary } from '@/bindings/SessionSummary'
import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { FormError } from '@/components/form-error'
import { CARD, Page } from '@/components/layout'
import { backOfficeKeys, sessionListQuery } from '@/features/back-office/api'
import { LiveIndicator } from '@/features/ordering/order-view'
import { minorUnits } from '@/lib/currencies'
import { cx } from '@/lib/cx'
import { useLanguage } from '@/lib/i18n/store'
import { useManagedBranch } from '@/features/back-office/scope'
import { useT } from '@/lib/i18n/translate'
import { branchEventsUrl, useBranchEvents } from '@/lib/live'
import { formatPrice } from '@/lib/menu-format'
import { formatWhen } from '@/lib/time'

export const SESSION_PATH = '/$restaurantSlug/$branchSlug/manage/sessions/$sessionId'

/**
 * Open dine-in sessions (PRD, G11): one card per seated table with its order counts and
 * running bill, each opening the session page. Live through G5's hints, polling behind
 * them. Every role in the branch.
 */
export function BranchSessionsPage() {
  const t = useT()
  const { restaurantId, branchId, context } = useManagedBranch()
  const queryClient = useQueryClient()
  const branch = context?.branches.find((candidate) => candidate.id === branchId)
  const live = useBranchEvents(context && branch ? branchEventsUrl(context.settings.slug, branch.slug) : undefined, () => {
    void queryClient.invalidateQueries({ queryKey: backOfficeKeys.sessions(restaurantId, branchId) })
  })
  const list = useQuery(sessionListQuery(restaurantId, branchId))


  return (
    <Page width="md" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">{t('sessions.title')}</h1>
        {branch && <span className="text-muted">{branch.name}</span>}
      </div>
      <LiveIndicator status={live} />
      {list.isPending ? (
        <p className="text-muted text-sm">{t('common.loading')}</p>
      ) : !list.data ? (
        <div className="flex flex-col gap-2">
          <FormError error={list.error} />
          <Button variant="secondary" onClick={() => void list.refetch()} className="self-start">
            {t('common.retry')}
          </Button>
        </div>
      ) : list.data.sessions.length === 0 ? (
        <p className="text-muted text-sm">{t('sessions.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {list.data.sessions.map((row) => (
            <li key={row.session.id}>
              <SessionCard row={row} currency={list.data.currency} />
            </li>
          ))}
        </ul>
      )}
    </Page>
  )
}

/** One seating with its counts and bill, opening the session page; also the search page's hit (G13). */
export function SessionCard({ row, currency }: { row: SessionSummary; currency: Currency }) {
  const t = useT()
  const { restaurantSlug, branchSlug } = useManagedBranch()
  const language = useLanguage()
  const title = t('track.sessionTitle', { name: row.session.table_name })
  const decimals = minorUnits(currency)
  return (
    <Link to={SESSION_PATH} params={{ restaurantSlug, branchSlug, sessionId: row.session.id }} className={cx(CARD, 'hover:bg-accent-soft flex flex-col gap-2 transition-colors')}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">{title}</h2>
          <p className="text-muted text-sm">
            {t('track.sessionOpenedAt', { time: formatWhen(row.session.created_at, language) })}
            {row.session.closed_at && <> · {t('track.sessionClosedAt', { time: formatWhen(row.session.closed_at, language) })}</>}
          </p>
        </div>
        {row.session.closed_at ? <Badge tone="muted">{t('sessions.closed')}</Badge> : row.active_count > 0 && <Badge tone="warning">{t('sessions.active', { count: row.active_count })}</Badge>}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-muted text-sm">{t('sessions.orders', { count: row.order_count })}</span>
        <span className="font-semibold tabular-nums">{formatPrice(row.total, decimals, language)}</span>
      </div>
    </Link>
  )
}
